import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router-dom'

import Button from '@components/shared/Button'
import Dialog from '@components/shared/Dialog'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import Tooltip from '@components/shared/Tooltip'
import AddedToCollectionDialog from '@components/shared/dialogs/collection/AddedToCollectionDialog'
import CollectionFormDialog from '@components/shared/dialogs/collection/CollectionFormDialog'
import ScrollSelector from '@components/shared/form/ScrollSelector'
import { useAsyncEffect } from '@hooks/index'
import { CollectionType } from '@models/Collection'
import { useAPIError } from '@providers/APIErrorProvider'
import CollectionService from '@services/CollectionService'
import ScoreService from '@services/ScoreService'
import TitleService from '@services/TitleService'
import { useDialogStore } from '@stores/dialog.store'

import type Collection from '@models/Collection'
import type { AddToCollectionResponse } from '@pages/home/actions/addToCollection.action'

type DialogData = { type: 'title' | 'scoreList' }
type TitleData = { type: 'title'; uuid: string } & DialogData
type ScoreListData = { type: 'scoreList'; ids: string[] } & DialogData

interface Props {
  data: TitleData | ScoreListData
}

const AddToCollectionDialog: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation()

  return (
    <>
      <Dialog name="add_to_collection" title={t('action.addToCollection')} maxWidth={364}>
        <AddToCollectionBody data={data} />
      </Dialog>
      <CollectionFormDialog />
      <AddedToCollectionDialog />
    </>
  )
}
export default AddToCollectionDialog

const AddToCollectionBody: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation()
  const openDialog = useDialogStore((s) => s.openDialog)
  const closeDialog = useDialogStore((s) => s.closeDialog)
  const { addError } = useAPIError()

  const fetcher = useFetcher<AddToCollectionResponse>()

  const [collections, setCollections] = useState<Collection[] | null>(null)
  const [usedCollections, setUsedCollections] = useState<number[] | null>(null)
  const [collectionId, setCollectionId] = useState<number | null>(null)

  useAsyncEffect(async (isActive) => {
    const collections = await CollectionService.fetchPrivateCollections()
    if (isActive()) setCollections(collections)

    let privateCollections: number[] = []
    if (data.type === 'title') {
      privateCollections = await TitleService.fetchPrivateCollectionsForTitle(data.uuid)
    } else if (data.type === 'scoreList') {
      privateCollections = await ScoreService.fetchPrivateCollectionsForScore(...data.ids)
    }
    if (isActive()) setUsedCollections(privateCollections)
  }, [])

  // When the fetcher completes adding to collection, handle response data
  useEffect(() => {
    if (!fetcher.data || fetcher.state !== 'idle') return

    const added = fetcher.data.collection_items.length > 0
    if (added) {
      const collection = collections?.find((col) => col.id === collectionId)
      openDialog('added_to_collection', { closeOthers: true, state: collection })
    } else {
      addError({
        code: 'errors.collectionAdd',
        message: t('errors.collectionAdd'),
      })
    }
  }, [fetcher.data, fetcher.state])

  const addToCollection = async (): Promise<void> => {
    if (!collectionId) return

    const submitData: { [name: string]: string } = { collectionId: collectionId.toString() }
    if (data.type === 'title') {
      submitData.collectionType = CollectionType.CONTENT_TITLE
      submitData.uuids = data.uuid
    } else if (data.type === 'scoreList') {
      submitData.collectionType = CollectionType.DOCUMENT
      submitData.scoreIds = data.ids.join(',')
    }

    fetcher.submit(submitData, {
      method: 'post',
      action: '/home/addToCollection',
    })
  }

  return (
    <>
      <p className="text-md mb-3">{t('messages.selectCollection')}</p>
      {collections && usedCollections ? (
        <>
          {collections.length > 0 && (
            <ScrollSelector
              items={collections.map((col) => ({
                text: col.name,
                value: col.id,
                disabled: usedCollections.includes(col.id),
              }))}
              onSelect={setCollectionId}
              className="mb-2"
              disabledIndicator={
                <Tooltip arrow placement="bottom" content={t('content.alreadyInCollection')}>
                  <i className="nf-icon nf-ok-sign text-primary" />
                </Tooltip>
              }
            />
          )}
        </>
      ) : (
        <LoadingSpinner />
      )}

      <button
        type="button"
        className="orange-link text-md"
        onClick={() => openDialog('add_collection', { closeOthers: true })}>
        {t('action.createCollection')}
      </button>
      <div className="flex flex-row w-full justify-end mt-4">
        <Button
          variant="secondary"
          className="mr-2"
          onClick={() => closeDialog('add_to_collection')}>
          {t('action.cancel')}
        </Button>
        <Button
          variant="primary"
          disabled={!collectionId || ['submitting', 'loading'].includes(fetcher.state)}
          onClick={addToCollection}>
          {t('action.save')}
        </Button>
      </div>
    </>
  )
}
